/**
 * GA4 / GTM eCommerce tracking module for adding eCommerce event tracking data to the Google Tag datalayer
 * and sending server side tracking to the GA4 Measurement Protocol in case the user has an ad blocker installed.
 *
 */

declare global {
	interface Window {
		crscTrackingData?: CrscTrackingData;
		dataLayer?: unknown[];
		jQuery?: any;
	}
}

interface CrscTrackingEvent {
	event: string;
	ecommerce?: Record<string, unknown>;
	[key: string]: unknown;
}

interface CrscTrackingData {
	currency: string;
	user: Record<string, unknown>;
	cart: {
		total: number;
		item_count: number;
		items: Array<Record<string, unknown>>;
	};
	events: CrscTrackingEvent[];
	tracking?: {
		restUrl: string;
		nonce: string;
		clientId: string;
		serverSideActive: boolean;
	};
}

/**
 * A minimal, storable representation of a cart line item, keyed by
 * `item_id` (product SKU, or id as a fallback — see `format_product_for_ga4()`).
 */
interface CartSnapshotItem {
	quantity: number;
	item: Record<string, unknown>;
}

/**
 * Shape of a single line item as returned by the WooCommerce Store API
 * (`/wp-json/wc/store/v1/cart`). Only the fields we actually use are typed.
 */
interface StoreApiCartItemCategory {
	id: number;
	name: string;
	slug: string;
}

interface StoreApiCartItem {
	id: number;
	sku: string;
	name: string;
	quantity: number;
	prices: {
		price: string;
		currency_minor_unit: number;
		currency_code: string;
	};
	extensions?: {
		'crsc-seo-marketing'?: {
			categories?: StoreApiCartItemCategory[];
			brand?: string;
			course_id?: string;
			class_product_name?: string;
		};
	};
}

interface StoreApiCart {
	items: StoreApiCartItem[];
	totals: {
		currency_code: string;
	};
}

/**
 * localStorage key used to persist the "current state" cart snapshot across
 * full (non-AJAX) page loads, so a classic "Add to cart" button/form that
 * fully reloads the page can still be detected as an add/remove.
 */
const CART_SNAPSHOT_STORAGE_KEY = 'crsc_ga4_cart_snapshot';

/**
 * Push an event onto the dataLayer for gtag.js / GTM to consume.
 */
function pushToDataLayer( event: CrscTrackingEvent ): void {
	window.dataLayer = window.dataLayer || [];
	// GA4 recommends clearing the previous ecommerce object before pushing a new one.
	window.dataLayer.push( { ecommerce: null } );
	window.dataLayer.push( event );
}

/**
 * Relay an event to our first-party collect endpoint so it can be forwarded
 * server-side via the GA4 Measurement Protocol.
 */
function sendServerSide( eventName: string, params: Record<string, unknown> ): void {
	const tracking = window.crscTrackingData?.tracking;

	if ( ! tracking || ! tracking.serverSideActive || ! tracking.restUrl ) {
		return;
	}

	const payload = JSON.stringify( { event: eventName, params } );

	if ( navigator.sendBeacon ) {
		const blob = new Blob( [ payload ], { type: 'application/json' } );
		navigator.sendBeacon( tracking.restUrl, blob );
		return;
	}

	fetch( tracking.restUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': tracking.nonce,
		},
		body: payload,
		keepalive: true,
	} ).catch( () => {
		// Silently ignore network errors; this is a best-effort signal.
	} );
}

/**
 * Track a single GA4 event both client-side (dataLayer) and server-side
 * (Measurement Protocol relay).
 *
 * @param event      The GA4 event to track.
 * @param serverSide Whether to also relay the event server-side via the
 *                    collect endpoint. Pass `false` when the server-side
 *                    Measurement Protocol event is already guaranteed to
 *                    fire independently (e.g. directly from a WooCommerce
 *                    PHP action hook), to avoid sending it twice.
 */
function trackEvent( event: CrscTrackingEvent, serverSide = true ): void {
	pushToDataLayer( event );

	if ( serverSide ) {
		sendServerSide( event.event, event.ecommerce || {} );
	}
}

/**
 * Fire all events generated server-side for the current page load.
 */
function trackInitialPageEvents( data: CrscTrackingData ): void {
	( data.events || [] ).forEach( ( event ) => trackEvent( event ) );
}

/**
 * Extract a WooCommerce product id from an "Add to cart" button element.
 */
function getProductIdFromElement( el: Element | null ): string {
	if ( ! el ) {
		return '';
	}

	return (
		el.getAttribute( 'data-product_id' ) ||
		el.getAttribute( 'data-product-id' ) ||
		''
	);
}

/**
 * Listen for classic WooCommerce (jQuery-driven) add-to-cart / remove-from-cart
 * events so eCommerce actions taken via AJAX are also tracked, in addition to
 * the events rendered server-side on full page loads. These fire on the
 * shortcode-based cart/checkout and classic "Add to cart" buttons/widgets.
 */
function bindWooCommerceClassicEvents(): void {
	const jQuery = window.jQuery;

	if ( ! jQuery ) {
		return;
	}

	jQuery( document.body ).on( 'added_to_cart', ( _event: unknown, _fragments: unknown, _cartHash: unknown, button: Element ) => {
		const productId = getProductIdFromElement( button );
		const quantityInput = button ? ( button as HTMLElement ).closest( 'form' )?.querySelector<HTMLInputElement>( 'input.qty' ) : null;
		const quantity = quantityInput ? parseInt( quantityInput.value, 10 ) || 1 : 1;

		if ( ! productId ) {
			return;
		}

		trackEvent( {
			event: 'add_to_cart',
			ecommerce: {
				currency: window.crscTrackingData?.currency || 'USD',
				items: [
					{
						item_id: productId,
						quantity,
					},
				],
			},
		} );

		// Keep the Store API baseline in sync so a later Blocks-based cart
		// change (which relies on diffing against this snapshot) doesn't
		// double-count this classic add-to-cart.
		void refreshStoreCartSnapshot();
	} );

	jQuery( document.body ).on( 'removed_from_cart', ( _event: unknown, _fragments: unknown, _cartHash: unknown, button: Element ) => {
		const productId = getProductIdFromElement( button );

		trackEvent( {
			event: 'remove_from_cart',
			ecommerce: {
				currency: window.crscTrackingData?.currency || 'USD',
				items: productId ? [ { item_id: productId, quantity: 1 } ] : [],
			},
		} );

		void refreshStoreCartSnapshot();
	} );
}

/**
 * Last known snapshot of the WooCommerce Store API cart, keyed by line item
 * id. Used to diff cart contents when the WooCommerce Blocks cart/checkout
 * (Cart, Checkout, Mini-Cart) mutates the cart, since those flows only ever
 * dispatch a plain notification event with no item details attached.
 */
let lastKnownCartItems: Map<number, StoreApiCartItem> | null = null;

/**
 * Fetch the current cart contents from the WooCommerce Store API. Used both
 * to seed the initial baseline and to resolve what changed after a Blocks
 * cart/checkout mutation event fires.
 */
async function fetchStoreApiCart(): Promise<StoreApiCart | null> {
	try {
		const response = await fetch( '/wp-json/wc/store/v1/cart', {
			credentials: 'same-origin',
			headers: { Accept: 'application/json' },
		} );

		if ( ! response.ok ) {
			return null;
		}

		return await response.json();
	} catch ( e ) {
		return null;
	}
}

/**
 * Refresh the stored cart baseline without emitting any tracking events.
 * Used to (re)sync state after a classic jQuery cart mutation, or to seed
 * the baseline on initial page load.
 */
async function refreshStoreCartSnapshot(): Promise<void> {
	const cart = await fetchStoreApiCart();

	if ( ! cart ) {
		return;
	}

	const items = new Map<number, StoreApiCartItem>();
	( cart.items || [] ).forEach( ( item ) => items.set( item.id, item ) );
	lastKnownCartItems = items;
}

/**
 * Convert a Store API cart line item into a GA4 eCommerce item entry.
 */
function storeApiItemToGa4Item( item: StoreApiCartItem, quantity: number ): Record<string, unknown> {
	const minorUnit = item.prices?.currency_minor_unit ?? 2;
	const price = item.prices?.price ? parseInt( item.prices.price, 10 ) / Math.pow( 10, minorUnit ) : 0;
	let product_id: string = String( item.id );

	if (item?.sku) {
		product_id = `SKU_${item.sku}`;
	}

	const ga4Item: Record<string, unknown> = {
		item_id: String( product_id ),
		item_name: item.name,
		quantity,
		price,
	};

	const brand = item.extensions?.[ 'crsc-seo-marketing' ]?.brand;

	if ( brand ) {
		ga4Item.item_brand = brand;
	}

	const categories = item.extensions?.[ 'crsc-seo-marketing' ]?.categories || [];

	// GA4 supports up to five category fields: item_category, item_category2..item_category5.
	categories.slice( 0, 5 ).forEach( ( category, index ) => {
		const key = index === 0 ? 'item_category' : `item_category${ index + 1 }`;
		ga4Item[ key ] = category.name;
	} );

	// If we have a course id and it's not empty, use that as the product id
	const course_id = item.extensions?.[ 'crsc-seo-marketing' ]?.course_id || false;

	if ( course_id ) {
		ga4Item.item_id = course_id;
	}

	// If we have a custom name for the item in the cart, use that as the product name instead of the one coming from WooCommerce
	const custom_item_name = item?.extensions?.[ 'crsc-seo-marketing' ]?.class_product_name || false;

	if (custom_item_name) {
		ga4Item['item_name'] = custom_item_name;
	}

	return ga4Item;
}

/**
 * Handle a WooCommerce Blocks `wc-blocks_added_to_cart` / `wc-blocks_removed_from_cart`
 * notification event. These events (dispatched as native `CustomEvent`s on
 * `document.body` by the Cart, Checkout and Mini-Cart blocks) carry no item
 * details, so we diff the Store API cart against our last known snapshot to
 * work out which line item(s) changed and by how much.
 */
async function handleBlocksCartEvent( eventName: 'add_to_cart' | 'remove_from_cart' ): Promise<void> {
	const cart = await fetchStoreApiCart();

	if ( ! cart ) {
		return;
	}

	const currentItems = new Map<number, StoreApiCartItem>();
	( cart.items || [] ).forEach( ( item ) => currentItems.set( item.id, item ) );

	if ( lastKnownCartItems ) {
		const changedItems: Array<Record<string, unknown>> = [];

		if ( eventName === 'add_to_cart' ) {
			currentItems.forEach( ( item, id ) => {
				const previousQuantity = lastKnownCartItems?.get( id )?.quantity || 0;

				if ( item.quantity > previousQuantity ) {
					changedItems.push( storeApiItemToGa4Item( item, item.quantity - previousQuantity ) );
				}
			} );
		} else {
			lastKnownCartItems.forEach( ( previousItem, id ) => {
				const currentQuantity = currentItems.get( id )?.quantity || 0;

				if ( currentQuantity < previousItem.quantity ) {
					changedItems.push( storeApiItemToGa4Item( previousItem, previousItem.quantity - currentQuantity ) );
				}
			} );
		}

		if ( changedItems.length ) {
			trackEvent( {
				event: eventName,
				ecommerce: {
					currency: cart.totals?.currency_code || window.crscTrackingData?.currency || 'USD',
					items: changedItems,
				},
			} );
		}
	}

	lastKnownCartItems = currentItems;
}

/**
 * Listen for the WooCommerce Blocks cart/checkout notification events. The
 * block-based Cart, Checkout and Mini-Cart blocks don't use jQuery/AJAX like
 * the classic shortcodes do — they call the Store API directly and, on
 * success, dispatch plain `CustomEvent`s on `document.body` so third parties
 * can react. Binding to `document.body` (rather than a specific block root)
 * ensures this keeps working regardless of which block triggered the change.
 */
function bindWooCommerceBlocksEvents(): void {
	document.body.addEventListener( 'wc-blocks_added_to_cart', () => {
		void handleBlocksCartEvent( 'add_to_cart' );
	} );

	document.body.addEventListener( 'wc-blocks_removed_from_cart', () => {
		void handleBlocksCartEvent( 'remove_from_cart' );
	} );
}

/**
 * Read the previously stored cart snapshot (if any) from localStorage.
 */
function readCartSnapshot(): Record<string, CartSnapshotItem> | null {
	try {
		const raw = window.localStorage.getItem( CART_SNAPSHOT_STORAGE_KEY );
		return raw ? JSON.parse( raw ) : null;
	} catch ( e ) {
		return null;
	}
}

/**
 * Delete the local cart snapshot from localstorage. Do this if the customer just made a purchase.
 *
 */
function deleteStoreCartSnapshot(): void {
	try {
		window.localStorage.removeItem( CART_SNAPSHOT_STORAGE_KEY );
	} catch ( e ) {
		console.error( e );
	}
}

/**
 * Persist the current cart snapshot to localStorage for comparison on the
 * next page load.
 */
function writeCartSnapshot( snapshot: Record<string, CartSnapshotItem> ): void {
	try {
		window.localStorage.setItem( CART_SNAPSHOT_STORAGE_KEY, JSON.stringify( snapshot ) );
	} catch ( e ) {
		// Ignore (e.g. private browsing / storage quota errors); worst case
		// we just lose diffing ability for classic full page reloads.
	}
}

/**
 * Build a snapshot of the current cart from the localized `cart.items` data
 * (which already carries the full, server-enriched GA4 item shape: SKU-based
 * `item_id`, `item_brand`, `item_category`..`item_category5`, etc.).
 */
function buildCartSnapshot( data: CrscTrackingData ): Record<string, CartSnapshotItem> {
	const snapshot: Record<string, CartSnapshotItem> = {};

	( data.cart?.items || [] ).forEach( ( item ) => {
		let itemId = String( item.item_id ?? '' );

		if (item?.sku) {
			itemId = `SKU_${item.sku}`;
		}

		if (item?.extensions ) {
			// If we have a course id and it's not empty, use that as the product id
			let course_id = item.extensions?.[ 'crsc-seo-marketing' ]?.course_id || false;

			if ( course_id ) {
				itemId = course_id;
			}
		}

		if ( ! itemId ) {
			return;
		}

		const quantity = Number( item.quantity ) || 0;
		const { cart_item_key, ...ga4Item } = item as Record<string, unknown> & { cart_item_key?: unknown };

		if (item?.extensions) {
			let custom_item_name = item?.extensions?.[ 'crsc-seo-marketing' ]?.class_product_name || false;

			if (custom_item_name) {
				ga4Item['item_name'] = custom_item_name;
			}
		}

		if ( snapshot[ itemId ] ) {
			snapshot[ itemId ].quantity += quantity;
		} else {
			snapshot[ itemId ] = { quantity, item: ga4Item };
		}
	} );

	return snapshot;
}

/**
 * Detect cart changes that happened via a classic, non-AJAX, full page
 * reload (e.g. a plain "Add to cart" form submit, or the classic cart page's
 * quantity/"Remove" links without JavaScript) by comparing the current cart
 * snapshot against the one stored in localStorage on a previous page load.
 *
 * Only pushes to `dataLayer` (for gtag.js/GTM); the corresponding
 * Measurement Protocol event is already sent server-side, unconditionally,
 * directly from the `woocommerce_add_to_cart` / `woocommerce_cart_item_removed`
 * PHP hooks, so relaying it again here would double-count it.
 */
function checkFullPageReloadCartDiff( data: CrscTrackingData ): void {
	const previousSnapshot = readCartSnapshot();
	const currentSnapshot = buildCartSnapshot( data );

	if ( previousSnapshot ) {
		const addedItems: Array<Record<string, unknown>> = [];
		const removedItems: Array<Record<string, unknown>> = [];

		Object.keys( currentSnapshot ).forEach( ( itemId ) => {
			const current = currentSnapshot[ itemId ];
			const previousQuantity = previousSnapshot[ itemId ]?.quantity || 0;

			if ( current.quantity > previousQuantity ) {
				addedItems.push( { ...current.item, quantity: current.quantity - previousQuantity } );
			}
		} );

		Object.keys( previousSnapshot ).forEach( ( itemId ) => {
			const previous = previousSnapshot[ itemId ];
			const currentQuantity = currentSnapshot[ itemId ]?.quantity || 0;

			if ( currentQuantity < previous.quantity ) {
				removedItems.push( { ...previous.item, quantity: previous.quantity - currentQuantity } );
			}
		} );

		if ( addedItems.length ) {
			trackEvent(
				{
					event: 'add_to_cart',
					ecommerce: { currency: data.currency, items: addedItems },
				},
				false
			);
		}

		if ( removedItems.length ) {
			trackEvent(
				{
					event: 'remove_from_cart',
					ecommerce: { currency: data.currency, items: removedItems },
				},
				false
			);
		}
	}

	writeCartSnapshot( currentSnapshot );
}

/**
 * Initialize GA4 eCommerce tracking.
 */
export function initTracking(): void {
	const data = window.crscTrackingData;

	if ( ! data ) {
		return;
	}

	// If the customer just made a purchase, delete the current snapshot of the cart to prevent firing off add to cart
	// delete cart events
	if (data.events && data.events.length) {
		data.events.some( ( eventData: Record<string, unknown> ) => {
			if ('purchase' === eventData.event) {
				deleteStoreCartSnapshot();
				return;
			}
		})
	}

	trackInitialPageEvents( data );
	checkFullPageReloadCartDiff( data );
	bindWooCommerceClassicEvents();
	bindWooCommerceBlocksEvents();

	// Seed the Store API cart baseline as soon as possible so that the first
	// Blocks cart/checkout mutation on this page load has something to diff
	// against (WooCommerce Blocks never expose item details on the event
	// itself).
	void refreshStoreCartSnapshot();
}
